import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { teamsLogin, loadTeamsUser, setTeamsAuthFromToken } from './store/slices/teamsAuthSlice';
import MainDashboard from './components/MainDashboard';
import './App.css';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentFailed from './components/PaymentFailed';

// Redux-powered Teams authentication app (matches Slack pattern)
function AppWithRedux() {
  const dispatch = useDispatch();
  
  // Redux state (same pattern as Slack)
  const { 
    teamsisAuthenticated, 
    teamsloading, 
    teamsuser, 
    teamserror 
  } = useSelector(state => state.teamsAuth);

  // Local state for MSAL
  const [msalStatus, setMsalStatus] = useState('loading');
  const [msalUser, setMsalUser] = useState(null);
  const [msalInstance, setMsalInstance] = useState(null);
  const [msalError, setMsalError] = useState(null);
  
  // URL parameters (same as Slack teams-pagebilling.js)
  const [initRef, setInitRef] = useState('null');
  const [initType, setInitType] = useState('null');
  
  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  // Extract URL parameters on component mount (same as Slack implementation)
  useEffect(() => {
    let search = window.location.search;
    let params = new URLSearchParams(search);
    let ref = params.get('ref');
    let firstType = params.get('type');

    setInitRef(ref === null ? 'null' : ref);
    setInitType(firstType === null ? 'null' : firstType);
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize MSAL
  useEffect(() => {
    const initializeMSAL = async () => {
      try {
        const { PublicClientApplication } = await import('@azure/msal-browser');
        
        const msalConfig = {
          auth: {
            clientId: process.env.REACT_APP_TEAMS_CLIENT_ID || "demo-client-id",
            authority: "https://login.microsoftonline.com/common",
            redirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: "sessionStorage",
            storeAuthStateInCookie: false,
          },
        };

        const instance = new PublicClientApplication(msalConfig);
        await instance.initialize();
        
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          setMsalUser(accounts[0]);
        }
        
        setMsalInstance(instance);
        setMsalStatus('ready');
      } catch (error) {
        console.error('MSAL initialization failed:', error);
        setMsalError(error.message);
        setMsalStatus('error');
      }
    };

    initializeMSAL();
  }, []);

  // Initialize Redux auth state from localStorage (same as Slack)
  useEffect(() => {
    dispatch(setTeamsAuthFromToken());
    
    // If we have a token, try to load user data
    const token = localStorage.getItem('teamstoken');
    if (token) {
      dispatch(loadTeamsUser());
    }
  }, [dispatch]);

  // Handle MSAL login and Redux integration
  const handleLogin = async () => {
    if (!msalInstance) {
      setMsalError('MSAL not initialized');
      return;
    }

    try {
      setMsalError(null);
      const loginRequest = {
        scopes: ["User.Read", "openid", "profile"],
        prompt: 'select_account'
      };

      const loginResponse = await msalInstance.loginPopup(loginRequest);
      
      if (loginResponse.account) {
        setMsalUser(loginResponse.account);

        // Get Teams context if available
        let teamsContext = {};
        try {
          if (window.microsoftTeams) {
            const { app } = await import('@microsoft/teams-js');
            await app.initialize();
            teamsContext = await app.getContext();
          }
        } catch (teamsError) {
          console.log('Teams SDK not available:', teamsError);
        }
        
        // Prepare auth data for Redux action (including URL parameters)
        const authData = {
          accessToken: loginResponse.accessToken,
          tenantId: loginResponse.account.tenantId,
          userId: loginResponse.account.localAccountId,
          teamId: teamsContext.team?.internalId || 'unknown',
          channelId: teamsContext.channel?.id || 'unknown',
          userPrincipalName: loginResponse.account.username,
          displayName: loginResponse.account.name,
          initRef: initRef,
          initType: initType
        };
        
        
        // Dispatch Teams login action (same pattern as Slack)
        try {
          const resultAction = await dispatch(teamsLogin(authData));
          
          // If we get here, login was successful
          dispatch(loadTeamsUser());
        } catch (loginError) {
          console.error('Login dispatch error:', loginError);
          setMsalError(`Authentication failed: ${loginError.message || loginError}`);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      setMsalError(`Login failed: ${error.message}`);
    }
  };

  // Handle logout (local only - don't sign out of Microsoft)
  // Logout is now handled by the MainDashboard component

  // Loading state
  if (msalStatus === 'loading' || teamsloading) {
    return (
      <div className="App">
        <nav className="navbar">
          <div className="container-fluid">
            <div className="navbar-brand">
              <img src="/assets/cheese-512.png" width={30} alt="Ricotta" style={{ marginRight: '0.5rem' }} />
              <span style={{ color: '#E04F60' }}>Ricotta</span>
            </div>
          </div>
        </nav>
        <main className="main-container">
          <div className="billing-card text-center">
            <div className="loading-spinner" style={{ width: '60px', height: '60px', margin: '2rem auto' }}></div>
            <h2>Initializing Teams Authentication...</h2>
            <p>Setting up secure login with Microsoft.</p>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (msalStatus === 'error') {
    return (
      <div className="App">
        <nav className="navbar">
          <div className="container-fluid">
            <div className="navbar-brand">
              <img src="/assets/cheese-512.png" width={30} alt="Ricotta" style={{ marginRight: '0.5rem' }} />
              <span style={{ color: '#E04F60' }}>Ricotta</span>
            </div>
          </div>
        </nav>
        <main className="main-container">
          <div className="billing-card text-center">
            <h1 className="dashboard-title" style={{ color: '#E04F60' }}>
              Authentication Setup Issue
            </h1>
            <p className="dashboard-subtitle">
              There's an issue with the Microsoft authentication setup.
            </p>
            
            {(msalError || teamserror) && (
              <div style={{ 
                background: 'rgba(224, 79, 96, 0.1)', 
                border: '1px solid rgba(224, 79, 96, 0.3)', 
                borderRadius: '12px', 
                padding: '1rem', 
                marginBottom: '2rem',
                color: '#E04F60'
              }}>
                <strong>Error:</strong> {msalError || teamserror}
              </div>
            )}
            
            <button 
              className="select-plan-btn"
              onClick={() => window.location.reload()}
            >
              🔄 Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Authentication screen - Split screen design like Slack but better
  if (!teamsisAuthenticated) {
    return (
      <div className="App" style={{ height: '100vh', overflow: 'hidden' }}>
        {/* Header */}
        <nav className="navbar" style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <div className="container-fluid">
            <div className="navbar-brand">
              <img src="/assets/cheese-512.png" width={30} alt="Ricotta" style={{ marginRight: '0.5rem' }} />
              <span style={{ color: '#E04F60', fontWeight: '700' }}>Ricotta</span>
            </div>
          </div>
        </nav>

        {/* Split Screen Layout */}
        <div style={{ 
          display: 'flex', 
          height: '100vh',
          paddingTop: '80px', // Account for navbar
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          
          {/* Left Side - Image */}
          <div style={{ 
            flex: isMobile ? '0 0 40vh' : '1',
            backgroundImage: 'url(/assets/Ricotta_Billing_2.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: window.innerWidth <= 768 ? '40vh' : 'auto'
          }}>
            {/* Overlay for better text readability */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(224, 79, 96, 0.85) 0%, rgba(44, 62, 80, 0.8) 100%)'
            }} />
            
            {/* Content overlay */}
            <div style={{ 
              position: 'relative', 
              zIndex: 2, 
              color: 'white', 
              textAlign: 'center',
              maxWidth: window.innerWidth <= 768 ? '90%' : '500px',
              padding: window.innerWidth <= 768 ? '1rem' : '2rem'
            }}>
              <h1 style={{ 
                fontSize: window.innerWidth <= 768 ? '2rem' : '3rem', 
                fontWeight: '800', 
                marginBottom: '1rem',
                textShadow: '0 3px 6px rgba(0,0,0,0.5)'
              }}>
                Team Building Made Fun
              </h1>
              <p style={{ 
                fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem', 
                marginBottom: window.innerWidth <= 768 ? '1rem' : '2rem',
                opacity: 0.95,
                lineHeight: '1.6',
                textShadow: '0 2px 4px rgba(0,0,0,0.4)'
              }}>
                Boost engagement with 17+ interactive games, trivia contests, and icebreakers designed for Microsoft Teams.
              </p>
              
              {/* Feature highlights */}
              <div style={{ 
                textAlign: 'left', 
                marginTop: window.innerWidth <= 768 ? '1rem' : '2rem',
                display: window.innerWidth <= 480 ? 'none' : 'block'
              }}>
                {[
                  { icon: '🎮', text: '17+ Interactive Games' },
                  { icon: '🧠', text: 'Weekly Trivia & Photo Quizzes' },
                  { icon: '❄️', text: 'Team Icebreakers & Social Games' }
                   
                ].map((feature, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: window.innerWidth <= 768 ? '0.5rem' : '1rem',
                    fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem'
                  }}>
                    <span style={{ 
                      fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem', 
                      marginRight: window.innerWidth <= 768 ? '0.75rem' : '1rem' 
                    }}>{feature.icon}</span>
                    <span style={{ textShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Authentication Form */}
          <div style={{ 
            flex: '1',
            background: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: window.innerWidth <= 768 ? '1.5rem' : '2rem',
            position: 'relative',
            minHeight: window.innerWidth <= 768 ? '60vh' : 'auto',
            overflow: window.innerWidth <= 768 ? 'auto' : 'visible'
          }}>
            <div style={{ 
              maxWidth: window.innerWidth <= 768 ? '100%' : '450px', 
              width: '100%',
              textAlign: 'center'
            }}>
              
              {/* Logo and Title */}
              <div style={{ marginBottom: window.innerWidth <= 768 ? '2rem' : '3rem' }}>
                <div style={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: window.innerWidth <= 768 ? '60px' : '80px',
                  height: window.innerWidth <= 768 ? '60px' : '80px',
                  background: '#ffffff',
                  borderRadius: window.innerWidth <= 768 ? '15px' : '20px',
                  marginBottom: window.innerWidth <= 768 ? '1rem' : '1.5rem',
                  boxShadow: '0 10px 30px rgba(224, 79, 96, 0.15)',
                  border: '1px solid rgba(224, 79, 96, 0.1)'
                }}>
                  <img src="/assets/cheese-512.png" width={window.innerWidth <= 768 ? 35 : 50} alt="Ricotta" />
                </div>
                
                <h2 style={{ 
                  color: '#2C3E50', 
                  fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem',
                  fontWeight: '700',
                  marginBottom: '0.5rem',
                  letterSpacing: '-0.025em'
                }}>
                  Ricotta Games on Teams
                </h2>
                <p style={{ 
                  color: '#6c757d', 
                  fontSize: window.innerWidth <= 768 ? '0.95rem' : '1.1rem',
                  marginBottom: '0',
                  lineHeight: '1.4'
                }}>
                  Manage your subscription for Ricotta Games, Trivia & Icebreakers on Microsoft Teams
                </p>
              </div>

              {/* Security Notice */}
              <div style={{
                background: 'rgba(224, 79, 96, 0.05)',
                border: '1px solid rgba(224, 79, 96, 0.15)',
                borderRadius: '16px',
                padding: window.innerWidth <= 768 ? '1rem' : '1.5rem',
                marginBottom: window.innerWidth <= 768 ? '1.5rem' : '2rem',
                textAlign: 'left'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ 
                    fontSize: window.innerWidth <= 768 ? '1.2rem' : '1.5rem', 
                    marginRight: window.innerWidth <= 768 ? '0.75rem' : '1rem' 
                  }}>🔐</span>
                  <div>
                    <h4 style={{ 
                      color: '#2C3E50', 
                      fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem'
                    }}>
                      Secure Microsoft Authentication
                    </h4>
                    <p style={{ 
                      color: '#6c757d', 
                      fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.9rem',
                      margin: '0',
                      lineHeight: '1.4'
                    }}>
                      Secure authentication for your billing management. Your credentials are never stored by Ricotta Games.
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {/* {(msalError || teamserror) && (
                <div style={{
                  background: 'rgba(220, 53, 69, 0.1)',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '2rem',
                  textAlign: 'left'
                }}>
                  <strong style={{ color: '#dc3545' }}>Authentication Error:</strong>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                    {msalError || teamserror}
                  </p>
                </div>
              )} */}

              {/* Sign In Button */}
              <button
                onClick={handleLogin}
                disabled={msalStatus !== 'ready'}
                style={{
                  width: '100%',
                  padding: window.innerWidth <= 768 ? '14px 20px' : '16px 24px',
                  background: msalStatus !== 'ready' 
                    ? '#e9ecef' 
                    : 'linear-gradient(135deg, #E04F60 0%, #c94456 100%)',
                  border: 'none',
                  borderRadius: '50px',
                  color: msalStatus !== 'ready' ? '#6c757d' : 'white',
                  fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
                  fontWeight: '600',
                  cursor: msalStatus !== 'ready' ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: msalStatus !== 'ready' 
                    ? 'none' 
                    : '0 8px 25px rgba(224, 79, 96, 0.4)',
                  marginBottom: window.innerWidth <= 768 ? '1.5rem' : '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseOver={(e) => {
                  if (msalStatus === 'ready') {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 12px 35px rgba(224, 79, 96, 0.5)';
                  }
                }}
                onMouseOut={(e) => {
                  if (msalStatus === 'ready') {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 8px 25px rgba(224, 79, 96, 0.4)';
                  }
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '12px' }}>
                  <path fill="currentColor" d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>
                </svg>
                {msalStatus !== 'ready' ? 'Initializing...' : 'Sign in with Microsoft'}
              </button>

              {/* Footer Info */}
              <div style={{ 
                fontSize: '0.85rem', 
                color: '#95a5a6',
                lineHeight: '1.5'
              }}>

                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Support:</strong> Questions? Email{' '}
                  <a href="mailto:cheese@ricotta.team" style={{ 
                    color: '#E04F60', 
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}>
                    cheese@ricotta.team
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard (authenticated) - Use the new dashboard layout
  // return (
  //   <MainDashboard 
  //     user={teamsuser?.user} 
  //     msalUser={msalUser}
  //   />
  // );

  return (
    <Router>
      <Routes>
        {/* Payment Routes */}
        <Route path="/paymentsuccess" element={<PaymentSuccess />} />
        <Route path="/paymentfailed" element={<PaymentFailed />} />
        
        {/* Main App Route */}
        <Route path="/" element={
          (
            <MainDashboard 
              user={teamsuser?.user} 
              msalUser={msalUser}
            />
          )
        } />
      </Routes>
    </Router>
  );
}

export default AppWithRedux;
